import React, { useContext, useEffect, useRef, useState } from "react";
import { getServerSession } from "next-auth/next";
import { getNextAuthOptions } from "../../api/auth/[...nextauth]";
import { InputText } from "primereact/inputtext";
import { LayoutContext } from "../../../layout/context/layoutcontext";
import { signIn } from "next-auth/react";
import { useSession } from "next-auth/react";
import { useRouter } from "next/router";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Password } from "primereact/password";
import { Checkbox } from "primereact/checkbox";
import { classNames } from "primereact/utils";
import { Toast } from "primereact/toast";
import { Tooltip } from "primereact/tooltip";
import { showError } from "../../../lib/GeneralFunction";
import Link from "next/link";

export async function getServerSideProps(context) {
  const sessionData = await getServerSession(context.req, context.res, getNextAuthOptions(context.req, context.res));
  if (sessionData?.user) {
    return {
      redirect: {
        permanent: false,
        destination: "/",
      },
    };
  }

  return {
    props: {},
  };
}

const initialValues = {
  email: "",
  password: "",
  remember_me: false,
};

const validationSchema = Yup.object().shape({
  email: Yup.string().email("Invalid email").required("harus diisi"),
  password: Yup.string().required("harus diisi"),
});

function LoginPage() {
  // Refs
  const toast = useRef(null);
  const mainContent = useRef(null);

  // Router and context
  const router = useRouter();
  const { error } = router.query;
  const { layoutConfig } = useContext(LayoutContext);

  // State management
  const [isScrolled, setIsScrolled] = useState(false);
  const [loading, setLoading] = useState(false);

  // Form handling
  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: handleLogin,
  });

  // Scroll effect
  useEffect(() => {
    const el = mainContent.current;
    if (el) {
      const isScrollable = el.scrollHeight > el.clientHeight;
      setIsScrolled(isScrollable);
    }
  }, []);

  // Handle login submission
  async function handleLogin(values) {
    setLoading(true);
    try {
      const login = await signIn("credentials", {
        email: values.email,
        password: values.password,
        remember_me: values.remember_me ? "true" : "false",
        redirect: false,
      });

      if (login?.error) {
        handleLoginError(login.error);
      } else {
        router.push("/");
      }
    } catch (error) {
      handleLoginError(error?.response?.data || error);
    } finally {
      setLoading(false);
    }
  }

  // Handle login errors
  function handleLoginError(error) {
    let errorMessage = "Terjadi kesalahan coba lagi nanti";

    if (typeof error === 'string') {
      if (error.includes("reason:")) {
        errorMessage = "Error Network";
      } else {
        errorMessage = error;
      }
    } else if (error.message) {
      errorMessage = error.message;
    }

    showError(toast, errorMessage);
  }

  return (
    <div className="w-full h-full">
      <Toast ref={toast} />
      <div className="min-h-screen flex flex-column md:flex-row" style={{ height: '100dvh' }} >
        <div className="hidden md:flex w-0 md:w-6 shadow-2 bg-blue-800 p-5 flex-column justify-content-between" style={{
          backgroundImage: `url(/layout/images/login-hero.jpg)`,
          backgroundSize: 'cover',
          backgroundPosition: 'top',
          width: '100%',
        }}>

          <div>
            <div className="text-blue-900 text-3xl font-bold mb-4">app.microva.id</div>
            <p className="text-blue-600 line-height-3">
              Selamat datang di marketplace utama aplikasi berbasis SaaS yang dimiliki oleh microva.id
            </p>
          </div>

          <div className="text-blue-600">
            <div className="flex align-items-center mb-3">
              <i className="pi pi-shield mr-2" />
              <span>Secure Programs</span>
            </div>
            <div className="flex align-items-center mb-3">
              <i className="pi pi-chart-line mr-2" />
              <span>Real-time Analytics</span>
            </div>
            <div className="flex align-items-center">
              <i className="pi pi-cog mr-2" />
              <span>Centralized Control</span>
            </div>
          </div>

        </div>

        <div ref={mainContent} className={`w-full md:w-6 p-5 flex align-items-${isScrolled ? 'start' : 'center'} justify-content-center`} style={{ overflowY: 'auto' }}>
          <div className="w-full" style={{ maxWidth: '500px' }}>
            <div className="shadow-3 card border-round-xl">
              <div className="text-center mb-6">
                <div className="flex justify-content-center mb-4">
                  <img
                    src={`/layout/images/godongpng.png`}
                    alt="Register Logo"
                    style={{ height: '60px' }}
                  />
                </div>
              </div>

              <div style={{ border: 'solid 1px rgba(0, 0, 0, 0.13)', width: '100%' }}></div>

              <div className="p-4">
                <form onSubmit={formik.handleSubmit} className="p-fluid">
                  {/* Email Field */}
                  <div className="field mb-4">
                    <label htmlFor="email" className="block text-900 font-medium mb-2">
                      Email
                    </label>
                    <InputText
                      id="email"
                      type="text"
                      name="email"
                      value={formik.values.email}
                      onChange={formik.handleChange}
                      className={classNames({ "p-invalid": formik.errors.email })}
                      placeholder="Email address"
                      style={{ padding: "1rem" }}
                    />
                    {formik.errors.email && <small className="p-error block">{formik.errors.email}</small>}
                  </div>

                  {/* Password Field */}
                  <div className="field mb-4">
                    <label htmlFor="password" className="block text-900 font-medium mb-2">
                      Password
                    </label>
                    <Password
                      id="password"
                      name="password"
                      value={formik.values.password}
                      onChange={(e) => formik.setFieldValue("password", e.target.value)}
                      toggleMask
                      feedback={false}
                      className={classNames({ "p-invalid": formik.errors.password })}
                      inputClassName="w-full"
                      placeholder="Password"
                    />
                    {formik.errors.password && <small className="p-error block">{formik.errors.password}</small>}
                  </div>

                  {/* Remember Me & Forgot Password */}
                  <div className="flex justify-content-between align-items-center mb-5">
                    <div className="flex align-items-center">
                      <Checkbox
                        inputId="remember_me"
                        name="remember_me"
                        checked={formik.values.remember_me}
                        onChange={(e) => {
                          formik.setFieldValue("remember_me", e.checked)
                        }}
                      />
                      <label htmlFor="remember_me" className="ml-2 text-600">
                        Remember me
                      </label>
                    </div>
                    <Link href="/#" className="text-primary font-medium no-underline hover:text-primary-600">
                      Forgot password?
                    </Link>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-2 mt-6">
                    <button
                      style={{
                        borderRadius: "2px",
                        padding: "0.75rem",
                        border: "none",
                      }}
                      className={`flex align-items-center justify-content-between gap-2 rounded bg-blue-500 text-white font-medium hover:bg-blue-600 disabled:bg-gray-400 transition w-full`}
                      disabled={loading}
                      type="submit"
                    >
                      <i className={`${loading ? "pi pi-spin pi-spinner" : "pi pi-user"}`}></i>
                      <span className="w-full">Sign In</span>
                    </button>

                    <Link
                      href={"/auth/register"}
                      style={{
                        borderRadius: "2px",
                        padding: "0.75rem",
                      }}
                      className={`flex align-items-center text-center justify-content-between gap-2 rounded bg-yellow-500 text-white font-medium hover:bg-yellow-600 transition w-full`}
                    >
                      <i className="pi pi-user-plus"></i>
                      <span className="w-full">Sign Up</span>
                    </Link>
                  </div>
                </form>
              </div>

              <div className="text-center mt-6">
                <span className="text-600 font-medium line-height-3">
                  © microva.id {new Date().getFullYear()} - All rights reserved
                </span>
                <div className="mt-2 flex justify-content-center gap-3">
                  <a href="#" className="text-600 hover:text-900"><i className="pi pi-globe"></i></a>
                  <a href="#" className="text-600 hover:text-900"><i className="pi pi-instagram"></i></a>
                  {/* <a href="#" className="text-600 hover:text-900"><i className="pi pi-github"></i></a> */}
                </div>
              </div>
            </div>
          </div>
        </div>


      </div>
    </div >
  );
}

LoginPage.getLayout = function LoginPage(page) {
  return <>{page}</>;
};

export default LoginPage;
